import { all, call, delay, put, select } from 'redux-saga/effects';
import { format } from 'date-fns';
import {
  getCSVToExport,
  getCSVToExportSuccess,
  getCSVToExportFailure,
  getStoplightById,
  getStoplightByIdSuccess,
  getStoplightByIdFailure,
  postNewStoplightSuccess,
  postNewStoplightFailure,
  updateStoplightSuccess,
  updateStoplightFailure,
  deleteStoplightSuccess,
  deleteStoplightFailure,
  shareStoplight,
  shareStoplightSuccess,
  shareStoplightFailure,
  postCommentToStoplightSuccess,
  postCommentToStoplightFailure,
  getStoplightsByLocationSuccess,
  getStoplightsByLocationMetricsSuccess,
  getStoplightsByLocationFailure,
  searchStoplightsSuccess,
  searchStoplightsFailure,
  saveTaskData,
  setToastOptions,
  slideIn
} from '../actions';

import { getData, postData, putData, deleteData } from '../../services/API';
import {
  baseStoplightUrl,
  discussionUrl,
  getStoplightsByLocationUrl,
  searchStoplightsUrl
} from '../../services/API/endpoints';

import { useStoplightsApi } from '../../utils/api';
import { date, text } from '../../utils/helpers';

// Stoplight sagas
export function* getStoplightByIdSaga({ payload }) {
  const url = `${baseStoplightUrl}/${payload}`;

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      return yield put(getStoplightByIdSuccess(data));
    }
    return yield put(getStoplightByIdFailure());
  } catch (error) {
    console.log(error);
    return yield put(getStoplightByIdFailure());
  }
}

export function* postNewStoplightSaga({ payload }) {
  const { authorizedUser } = yield select(state => state.AuthorizedUser);
  const { selectedUnits } = yield select(state => state.ConfigReducer);
  const {
    currentStoplightPage,
    sortDir,
    sortKey,
    stoplightFilter,
    stoplightSearchQuery,
    stoplightSharingQ
  } = yield select(state => state.StoplightReducer);
  const tempTask = yield select(state => state.TaskReducer.tempTaskObject);
  const { patientSummaryReportFilter } = yield select(
    state => state.ReportReducer
  );
  const { employeeSummaryReportFilter } = yield select(
    state => state.ReportReducer
  );

  const url = `${baseStoplightUrl}`;

  try {
    const { data, status } = yield call(postData, url, payload);
    if (status === 200) {
      yield put(
        setToastOptions({
          msg: 'Stoplight Posted',
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      yield delay(2100);
      yield put(postNewStoplightSuccess(data));
      // add stoplightId to store task from here
      if (Object.keys(tempTask).length > 0) {
        yield put(saveTaskData({ ...tempTask, stoplightId: data.id }));
      }
      // Only call the share saga in there are users in the Q to share task with
      if (stoplightSharingQ.email?.length > 0) {
        const sharingQ = { ...stoplightSharingQ, id: data.id };
        yield put(shareStoplight(sharingQ));
        yield put(
          setToastOptions({
            msg: 'Stoplight has been shared!',
            feedback: 'success',
            duration: 1000,
            speed: 500
          })
        );
        yield put(slideIn());
      }
      let stoplightAction;

      if (window.location.pathname.includes('/summary')) {
        let tempStoplightFilter = {
          category: '',
          subCategory: '',
          createdBy: '',
          hasAttachedIssues: false,
          hasUpdate: false
        };
        if (window.location.pathname.includes('patient/summary')) {
          tempStoplightFilter = {
            ...tempStoplightFilter,
            ...patientSummaryReportFilter,
            startDate:
              patientSummaryReportFilter.startDate ??
              format(new Date(), 'yyyy-MM-dd')
          };
        } else {
          tempStoplightFilter = {
            ...tempStoplightFilter,
            ...employeeSummaryReportFilter,
            startDate:
              employeeSummaryReportFilter.startDate ??
              format(new Date(), 'yyyy-MM-dd')
          };
        }

        stoplightAction = useStoplightsApi({
          authorizedUser,
          currentStoplightPage,
          searchTerm: stoplightSearchQuery,
          selectedUnits,
          sortKey,
          sortDir,
          stoplightFilter: tempStoplightFilter,
          summaryView: 'patient'
        });
      } else {
        stoplightAction = useStoplightsApi({
          authorizedUser,
          currentStoplightPage,
          searchTerm: stoplightSearchQuery,
          selectedUnits,
          sortKey,
          sortDir,
          stoplightFilter
        });
      }
      return yield put(stoplightAction);
    }
    yield put(
      setToastOptions({
        msg: 'Stoplight Failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(postNewStoplightFailure());
  } catch (error) {
    console.log(error);
    yield put(
      setToastOptions({
        msg: 'Stoplight Failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(postNewStoplightFailure());
  }
}

export function* updateStoplightSaga({ payload }) {
  const { authorizedUser } = yield select(state => state.AuthorizedUser);
  const { hcaid, firstName, lastName } = authorizedUser;
  const { selectedUnits } = yield select(state => state.ConfigReducer);
  const {
    currentStoplightPage,
    sortDir,
    sortKey,
    stoplightFilter,
    stoplightSearchQuery
  } = yield select(state => state.StoplightReducer);

  const url = `${baseStoplightUrl}`;

  try {
    const { data: currentStoplight } = yield call(
      getData,
      `${url}/${payload.id}`
    );
    const { data, status } = yield call(putData, url, payload);
    if (status === 200) {
      const removedIssues = currentStoplight?.issues?.filter(
        val => !payload.issueIds.find(issueId => val.taskId === issueId)
      );
      if (removedIssues && removedIssues.length > 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (const issue of removedIssues) {
          const commentPayload = {
            body: `Issue ${
              issue.friendlyId || '------'
            } was removed from Stoplight ${payload.friendlyId || '------'}`,
            authorId: hcaid,
            authorFullName: `${firstName} ${lastName}`
          };
          // eslint-disable-next-line no-await-in-loop
          yield call(
            postData,
            `${discussionUrl}/${payload.id}/comment`,
            commentPayload
          );
        }
        console.log(`${removedIssues.length} issues removed!`);
      }
      // appInsights.trackEvent({
      //   name: 'Stoplight_Edited'
      // });
      yield put(updateStoplightSuccess(data));
      yield put(
        setToastOptions({
          msg: 'Stoplight Updated',
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      yield delay(2100);
      let sharingQ = yield select(
        state => state.StoplightReducer.stoplightSharingQ
      );
      sharingQ = { ...sharingQ, id: data.id };
      // Only call the share saga in there are users in the Q to share task with
      if (sharingQ.email.length > 0) {
        yield call(shareStoplight, sharingQ);
        yield put(
          setToastOptions({
            msg: 'Stoplight has been shared!',
            feedback: 'success',
            duration: 1000,
            speed: 500
          })
        );
        yield put(slideIn());
      }
      const stoplightAction = useStoplightsApi({
        authorizedUser,
        currentStoplightPage,
        searchTerm: stoplightSearchQuery,
        selectedUnits,
        sortKey,
        sortDir,
        stoplightFilter
      });
      return yield put(stoplightAction);
    }
    if (status === 400) {
      yield put(
        setToastOptions({
          msg: `Failed to Update Stoplight! Status: ${status}!`,
          feedback: 'error',
          duration: null,
          speed: 500,
          question: true
        })
      );
      yield put(slideIn());
      return yield put(updateStoplightFailure());
    }
    return undefined;
  } catch (error) {
    console.log(error);
    yield put(
      setToastOptions({
        msg: 'Stoplight Failed to Update!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(updateStoplightFailure());
  }
}

export function* deleteStoplightSaga({ payload }) {
  const { authorizedUser } = yield select(state => state.AuthorizedUser);
  const { selectedUnits } = yield select(state => state.ConfigReducer);
  const {
    currentStoplightPage,
    sortDir,
    sortKey,
    stoplightFilter,
    stoplightSearchQuery
  } = yield select(state => state.StoplightReducer);

  const url = `${baseStoplightUrl}/${payload}`;

  try {
    const { data, status } = yield call(deleteData, url);
    if (status === 200) {
      yield put(
        setToastOptions({
          msg: 'Stoplight Deleted!',
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      yield put(deleteStoplightSuccess(data));
      const stoplightAction = useStoplightsApi({
        authorizedUser,
        currentStoplightPage,
        searchTerm: stoplightSearchQuery,
        selectedUnits,
        sortKey,
        sortDir,
        stoplightFilter
      });
      return yield put(stoplightAction);
    }
    yield put(
      setToastOptions({
        msg: 'Stoplight Failed to Delete!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(deleteStoplightFailure());
  } catch (error) {
    console.log(error);
    yield put(
      setToastOptions({
        msg: 'Stoplight Failed to Delete!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(deleteStoplightFailure());
  }
}

export function* postCommentToStoplightSaga({ payload }) {
  const { id, comment } = payload;
  const { authorizedUser } = yield select(state => state.AuthorizedUser);
  const { selectedUnits } = yield select(state => state.ConfigReducer);
  const {
    currentStoplightPage,
    sortDir,
    sortKey,
    stoplightFilter,
    stoplightSearchQuery
  } = yield select(state => state.StoplightReducer);

  const url = `${discussionUrl}/${id}/comment`;

  try {
    const { data, status } = yield call(postData, url, comment);

    if (status === 200) {
      yield put(
        setToastOptions({
          msg: 'Comment Posted',
          feedback: 'success',
          duration: 1000,
          speed: 500
        })
      );
      yield put(slideIn());
      yield put(postCommentToStoplightSuccess(data));
      yield put(getStoplightById(id));
      const stoplightAction = useStoplightsApi({
        authorizedUser,
        currentStoplightPage,
        searchTerm: stoplightSearchQuery,
        selectedUnits,
        sortKey,
        sortDir,
        stoplightFilter
      });
      return yield put(stoplightAction);
    }
    yield put(
      setToastOptions({
        msg: 'Comment Failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(postCommentToStoplightFailure());
  } catch (error) {
    console.log(error);
    yield put(
      setToastOptions({
        msg: 'Comment Failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    yield put(slideIn());
    return yield put(postCommentToStoplightFailure());
  }
}

export function* getStoplightsByLocationSaga({ payload }) {
  const { location, filterData } = payload;
  const params = new URLSearchParams(filterData).toString();
  let url = `${getStoplightsByLocationUrl}?${params}`;

  if (location === 'reports') {
    url = `${url}&metricsOnly=true`;
  }

  if (filterData.locationIds.length === 0)
    return yield put(
      getStoplightsByLocationSuccess({
        results: [],
        totalCount: 0
      })
    );
  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      if (location === 'reports') {
        return yield put(
          getStoplightsByLocationMetricsSuccess({
            totalRecordsCount: data.totalCount,
            completedRecordsCount: data.completeCount ?? 0,
            openedRecordsCount: data.openCount ?? 0,
            deferredRecordsCount: data.deferredCount ?? 0
          })
        );
      }
      return yield put(getStoplightsByLocationSuccess(data));
    }
    return yield put(getStoplightsByLocationFailure());
  } catch (error) {
    console.log(error);
    return yield put(getStoplightsByLocationFailure());
  }
}

export function* getStoplightsByLocationDownloadSaga({ payload }) {
  let totalData = [];
  yield put(getCSVToExport());

  try {
    const { data, status } = yield call(
      getData,
      `${getStoplightsByLocationUrl}?${new URLSearchParams({
        ...payload,
        page: 1
      }).toString()}`
    );
    if (status === 200) {
      if (data.totalCount > 50) {
        const totalPages = Math.ceil(data.totalCount / 50);
        const restData = yield all(
          [...Array(totalPages + 1).keys()].slice(2).map(page =>
            call(
              getData,
              `${getStoplightsByLocationUrl}?${new URLSearchParams({
                ...payload,
                page
              }).toString()}`
            )
          )
        );
        if (restData && restData.length > 0) {
          totalData = [
            ...data.results,
            ...restData.reduce(
              (acc, response) => acc.concat(response.data.results),
              []
            )
          ];
        }
      } else {
        totalData = [...data.results];
      }
    } else {
      yield put(getCSVToExportFailure());
    }
  } catch (err) {
    yield put(getCSVToExportFailure());
  }
  yield put(
    getCSVToExportSuccess(
      totalData.map(item => {
        const tempItem = item;
        delete tempItem?.id;
        delete tempItem?.completedDate;
        delete tempItem?.discussion;
        delete tempItem?.discussionId;
        delete tempItem?.issues;
        delete tempItem?.partitionKey;
        delete tempItem?.sharedWith;
        return {
          ...tempItem,
          createdDate: date.dayOfYrFull(item.created),
          title: text.escCSVChars(tempItem.title),
          details: text.escCSVChars(tempItem.details),
          attachedIssues: tempItem?.issueIds,
          facility: tempItem.locations?.facility[0]?.displayName,
          latestComment: tempItem.latestComment
            ? `${
                tempItem.latestComment.date &&
                date.dayOfYr(tempItem.latestComment.date)
              } | ${tempItem.latestComment.authorFullName} ${text.escCSVChars(
                tempItem.latestComment.body
              )}`
            : '',
          location:
            tempItem.origin === 'employee'
              ? tempItem.locations?.department[0]?.displayName
              : tempItem.origin === 'patient'
              ? tempItem.locations?.unit[0]?.displayName
              : ''
        };
      })
    )
  );
}

export function* searchStoplightsSaga({ payload }) {
  const { query, page, origin, facilityId } = payload;

  const url = `${searchStoplightsUrl}?facilityId=${facilityId}&query=${query}&origin=${origin}&page=${page}`;

  try {
    const { data, status } = yield call(getData, url);
    if (status === 200) {
      return yield put(searchStoplightsSuccess(data));
    }
    return yield put(searchStoplightsFailure());
  } catch (error) {
    console.log(error);
    return yield put(searchStoplightsFailure());
  }
}

export function* shareStoplightSaga({ payload }) {
  const url = `${baseStoplightUrl}/share`;
  try {
    const { status } = yield call(postData, url, payload);
    if (status === 200) {
      return yield put(shareStoplightSuccess());
    }
    yield put(
      setToastOptions({
        msg: 'Share failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  } catch (error) {
    console.log(error);
    yield put(shareStoplightFailure(error));
    yield put(
      setToastOptions({
        msg: 'Share failed to Post!',
        feedback: 'error',
        duration: null,
        speed: 500,
        question: true
      })
    );
    return yield put(slideIn());
  }
}
